import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight, Globe, ShieldCheck, CreditCard, Repeat, Link2,
  ShoppingCart, Lock, BarChart3, CheckCircle, Zap,
} from "lucide-react";
import ContactForm from "@/components/ContactForm";
import RelatedReviews from "@/components/RelatedReviews";
import LocationCrossLinks from "@/components/LocationCrossLinks";
import FormReviewCompanion from "@/components/FormReviewCompanion";
import Breadcrumbs from "@/components/Breadcrumbs";
import { useProductViewed } from "@/utils/trackingHooks";
import SEOHead from "@/components/SEOHead";
import WhatsAppButton from "@/components/WhatsAppButton";
import { fadeUp, stagger, heroImageMotion, viewportOnce, viewportOnceSmall } from "@/utils/motionVariants";

const FEATURES = [
  { icon: CreditCard, title: "All Major Cards & Wallets", desc: "Accept Visa, Mastercard, Amex, Apple Pay, Google Pay and more through a single gateway." },
  { icon: Lock, title: "PCI DSS Compliant", desc: "Built-in fraud protection, 3D Secure authentication, and full PCI compliance for peace of mind." },
  { icon: Link2, title: "Pay by Link", desc: "Send secure payment links via email or SMS. Perfect for invoicing, deposits, and remote payments." },
  { icon: Repeat, title: "Recurring Payments", desc: "Set up subscriptions and repeat billing automatically. Ideal for memberships and services." },
  { icon: ShoppingCart, title: "E-Commerce Integration", desc: "Seamless plug-ins for WooCommerce, Shopify, Magento and custom-built websites." },
  { icon: BarChart3, title: "Real-Time Dashboard", desc: "Track transactions, settlements, and refunds from a clear online dashboard." },
];

const USE_CASES = [
  { title: "Online Stores", desc: "Sell products with a smooth, branded checkout that reduces basket abandonment and builds trust.", icon: ShoppingCart },
  { title: "Service Businesses", desc: "Take deposits, send invoices, and accept payments remotely with Pay by Link and virtual terminal.", icon: Zap },
  { title: "Subscription Models", desc: "Automate recurring payments for memberships, software licenses, or subscription boxes.", icon: Repeat },
  { title: "Multi-Channel Sellers", desc: "Sync online and in-store payments through your EPOS for unified reporting and stock management.", icon: Globe },
];

export default function OnlinePaymentsPage() {
  useProductViewed("online_payments");
  return (
    <div>
      <SEOHead title="Online Payment Solutions for UK Businesses" description="Explore seamless online payment solutions for businesses in the UK. CanDo Payments helps you accept digital transactions securely, boosting your sales revenue." path="/online-payments" service={{ name: "Online Payments", description: "PCI-compliant online payment processing for UK e-commerce. Accepts all major cards, Apple Pay, Google Pay and alternative methods.", serviceType: "Online Payment Processing" }} />
      {/* Hero */}
      <section className="hero-gradient py-16 md:py-24" data-testid="online-payments-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" animate="visible" variants={stagger}>
              <Breadcrumbs className="mb-5" />
              <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">Online Payments</motion.p>
              <motion.h1 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold text-brand-black tracking-tight leading-tight mb-6">
                Secure online payments, <span className="text-brand-aqua">without the headache</span>
              </motion.h1>
              <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed mb-8">
                Accept card payments on your website with a fast, secure gateway that your customers trust. Simple setup, clear pricing, and UK-based support when you need it.
              </motion.p>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
                <Link to="/contact?product=Online%20Payments&category=online-payments&source=online-payments-page" className="bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 transition-colors" data-testid="online-payments-cta-quote">
                  Get Started <ArrowRight size={16} />
                </Link>
                <Link to="/quote/online" className="bg-brand-pink-light text-brand-pink hover:bg-brand-pink/15 border border-brand-pink/30 font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 transition-colors" data-testid="online-payments-cta-estimate">
                  Get an Instant Estimate
                </Link>
                <WhatsAppButton variant="secondary" placement="online" label="WhatsApp us" />
              </motion.div>
            </motion.div>
            <motion.div
              {...heroImageMotion}
              className="hidden lg:block"
            >
              <img
                src="https://cdn.candopayments.com/marketing/heroes/online-payments_colour_web_solid.jpg"
                alt="Online Payments"
                className="w-full rounded-2xl shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="py-12 bg-white border-b border-brand-light-grey/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: ShieldCheck, label: "PCI DSS Compliant" },
              { icon: Lock, label: "3D Secure" },
              { icon: Globe, label: "Multi-Currency" },
              { icon: Zap, label: "Instant Setup" },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-3 p-4 rounded-xl bg-brand-aqua-light">
                <item.icon size={20} className="text-brand-aqua flex-shrink-0" />
                <span className="text-sm font-semibold text-brand-black">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-20 md:py-28 bg-gray-50" data-testid="online-payments-features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="text-center mb-14">
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">Features</motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight">
              Everything you need to sell online
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnceSmall} variants={stagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f) => (
              <motion.div key={f.title} variants={fadeUp} className="bg-white border border-brand-light-grey rounded-2xl p-8 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 service-card" data-testid={`online-feature-${f.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="w-12 h-12 rounded-xl bg-brand-aqua-light flex items-center justify-center mb-5">
                  <f.icon size={22} className="text-brand-aqua" />
                </div>
                <h3 className="font-heading text-lg font-bold text-brand-black mb-2">{f.title}</h3>
                <p className="text-sm text-brand-grey leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 md:py-28 bg-white" data-testid="online-payments-use-cases">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="text-center mb-14">
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">Use Cases</motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight">
              Built for how you sell
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnceSmall} variants={stagger} className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {USE_CASES.map((uc) => (
              <motion.div key={uc.title} variants={fadeUp} className="flex gap-5 bg-white border border-brand-light-grey rounded-2xl p-6 shadow-sm" data-testid={`use-case-${uc.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="w-12 h-12 rounded-xl bg-brand-aqua-light flex items-center justify-center flex-shrink-0">
                  <uc.icon size={22} className="text-brand-aqua" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-brand-black mb-2">{uc.title}</h3>
                  <p className="text-sm text-brand-grey leading-relaxed">{uc.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-gray-50" data-testid="online-payments-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="font-heading text-3xl font-bold text-brand-black tracking-tight mb-6">
                Start accepting online payments today
              </h2>
              <p className="text-brand-grey leading-relaxed mb-6">
                Whether you&apos;re launching your first online shop or switching from another provider, we make the process simple. Setup is straightforward, migration is handled for you, and our UK-based team is on hand whenever you need support.
              </p>
              <div className="space-y-3">
                {[
                  "No complex development work required",
                  "Works with all major e-commerce platforms",
                  "Clear pricing with no hidden fees",
                  "UK-based support team",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-sm text-brand-black">
                    <CheckCircle size={16} className="text-brand-aqua flex-shrink-0" />{item}
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-5">
              <div className="bg-white border border-brand-light-grey rounded-2xl p-6 sm:p-8 shadow-sm">
                <h3 className="font-heading text-xl font-bold text-brand-black mb-4">Get your online payments setup</h3>
                <ContactForm />
              </div>
              <FormReviewCompanion product="online-payments" testId="online-payments-form-review" />
            </div>
          </div>
        </div>
      </section>

      <LocationCrossLinks />
      <RelatedReviews
        product="online-payments"
        eyebrow="Customer Stories"
        heading="See what our customers say about our online payments"
        subheading="Real feedback from UK businesses taking payments online with CanDo."
        testId="online-payments-related-reviews"
      />
    </div>
  );
}
